/** A nav entry as far as current-route resolution is concerned. */
export interface MatchableNavItem {
  label: string;
  path: string;
  /** Extra paths that should also mark this entry current, e.g. its child routes' parent. */
  alsoMatches?: string[];
  /** Only mark current on an exact match. Use for index routes and "new" forms. */
  exact?: boolean;
}

const normalize = (path: string) => (path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path);

/** Every route is mounted under both `/` and `/agent/`, so compare without the prefix. */
export const stripAgentPrefix = (pathname: string) => pathname.replace(/^\/agent/, '') || '/';

const matchLength = (item: MatchableNavItem, currentPath: string): number => {
  const candidates = [item.path, ...(item.alsoMatches ?? [])];

  const lengths = candidates.map((candidate) => {
    const path = normalize(candidate);

    if (path === currentPath) return path.length;
    if (item.exact) return 0;
    // A prefix only counts on a segment boundary, so /billing/remits never claims /billing/remit.
    if (path !== '/' && currentPath.startsWith(`${path}/`)) return path.length;

    return 0;
  });

  return Math.max(...lengths);
};

/**
 * Picks the single entry that owns the current route.
 *
 * Longest match wins, so a specific entry (`/billing/remit/new`) beats a broader sibling
 * (`/billing/remits` claiming the remit edit routes) instead of both lighting up.
 */
const resolveCurrentNavLabel = (
  items: MatchableNavItem[],
  pathname: string
): string | undefined => {
  const currentPath = normalize(stripAgentPrefix(pathname));

  let winner: string | undefined;
  let best = 0;

  items.forEach((item) => {
    const length = matchLength(item, currentPath);

    if (length > best) {
      best = length;
      winner = item.label;
    }
  });

  return winner;
};

export default resolveCurrentNavLabel;
